import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';

import { PageSubHeader, PageSubHeaderTabs } from '@/components/organisms/PageSubHeader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { selectAccountSlug } from '@/redux/session/selectors';

import { CampaignBreadcrumbs } from '../../components/CampaignBreadcrumbs';
import { useCampaigns } from '../../hooks/useCampaigns';

type CampaignTab = 'overview' | 'performance' | 'conversations';

/** The route segment each tab navigates to. `overview` keeps its legacy `analytics` path. */
const TAB_SEGMENTS: Record<CampaignTab, string> = {
  overview: 'analytics',
  performance: 'performance',
  conversations: 'logs',
};

export const getActiveTab = (pathname: string): CampaignTab => {
  const segment = pathname.replace(/\/+$/, '').split('/').pop();

  if (segment === 'logs') {
    return 'conversations';
  }
  if (segment === 'performance') {
    return 'performance';
  }
  return 'overview';
};

export const CampaignDetailLayout = () => {
  const { t } = useTranslation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);

  const { campaign, getStatus } = useCampaigns(campaignId);

  const activeTab = getActiveTab(pathname);

  const isCampaignLoaded = getStatus !== 'pending';

  // Matched on the exact value, not `!== 'marketing'`: other debt-less types exist.
  const isRecoveryCampaign = campaign?.type === 'payment_recovery';

  const handleTabChange = (value: string) => {
    const tab = value as CampaignTab;
    navigate(`/${slug}/campaigns/${campaignId}/${TAB_SEGMENTS[tab]}`);
  };

  // A bookmarked /performance URL on a campaign with no debt data — send it to
  // Overview rather than leave the tab strip with no matching trigger.
  if (isCampaignLoaded && activeTab === 'performance' && !isRecoveryCampaign) {
    return <Navigate replace to={`/${slug}/campaigns/${campaignId}/${TAB_SEGMENTS.overview}`} />;
  }

  return (
    <>
      {/* No actions slot here, so below lg we shrink PageSubHeader's trailing 1fr column to auto — the empty track collapses and the tabs sit flush right instead of centered. */}
      <PageSubHeader
        inlineTabs
        className="max-lg:grid-cols-[minmax(0,1fr)_auto_auto]"
        breadcrumbs={<CampaignBreadcrumbs campaignName={campaign?.name} />}
        tabs={
          <PageSubHeaderTabs>
            {/* Manual activation: these tabs navigate, so activating on focus would
                push a duplicate history entry per click and change route on arrow keys. */}
            <Tabs activationMode="manual" value={activeTab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="overview">{t('campaigns.tabs.overview')}</TabsTrigger>
                {/* Gate only this trigger — gating the strip blanks the header on every load. */}
                {isCampaignLoaded && isRecoveryCampaign && (
                  <TabsTrigger value="performance">{t('campaigns.tabs.performance')}</TabsTrigger>
                )}
                <TabsTrigger value="conversations">{t('campaigns.tabs.conversations')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </PageSubHeaderTabs>
        }
      />
      <Outlet />
    </>
  );
};
